import { useMemo } from 'react';
import { format } from 'date-fns';
import {
  enUS, es, fr, de, it, nl, ru, ja, ko, ar, ptBR, pt, zhCN,
} from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

type Locale = typeof enUS;

const localeMap: Record<string, Locale> = {
  'en': enUS,
  'es': es,
  'fr': fr,
  'de': de,
  'it': it,
  'nl': nl,
  'ru': ru,
  'ja': ja,
  'ko': ko,
  'ar': ar,
  'pt-BR': ptBR,
  'pt-PT': pt,
  'zh-CN': zhCN,
};

const formatMap: Record<string, string> = {
  'en': 'MM/dd/yyyy',
  'es': 'dd/MM/yyyy',
  'fr': 'dd/MM/yyyy',
  'de': 'dd.MM.yyyy',
  'it': 'dd/MM/yyyy',
  'nl': 'dd-MM-yyyy',
  'ru': 'dd.MM.yyyy',
  'ja': 'yyyy/MM/dd',
  'ko': 'yyyy-MM-dd',
  'ar': 'dd/MM/yyyy',
  'pt-BR': 'dd/MM/yyyy',
  'pt-PT': 'dd/MM/yyyy',
  'zh-CN': 'yyyy-MM-dd',
};

function resolveKey(lang: string): string {
  if (!lang) return 'en';
  if (localeMap[lang]) return lang;
  const base = lang.split('-')[0].toLowerCase();
  if (localeMap[base]) return base;
  const regional = Object.keys(localeMap).find((k) => k.toLowerCase().startsWith(base + '-'));
  return regional || 'en';
}

/**
 * Custom hook for managing date picker locale functionality.
 * Pulls the locale from the active i18n language (reactive to language changes).
 */
export const useDatePickerLocale = (localeOverride?: string) => {
  const { i18n } = useTranslation();

  const resolvedLocale = useMemo(() => {
    if (localeOverride && localeMap[localeOverride]) return localeOverride;
    return resolveKey(i18n.language || 'en');
  }, [localeOverride, i18n.language]);

  const dateLocale = useMemo(() => localeMap[resolvedLocale] || enUS, [resolvedLocale]);
  const dateFormat = useMemo(() => formatMap[resolvedLocale] || 'MM/dd/yyyy', [resolvedLocale]);

  const formatDisplayValue = useMemo(() => {
    return (date: Date | null): string => {
      if (!date) return '';
      try {
        return format(date, dateFormat, { locale: dateLocale });
      } catch (error) {
        console.warn('Date formatting failed, using ISO format:', error);
        return format(date, 'yyyy-MM-dd');
      }
    };
  }, [dateFormat, dateLocale]);

  const parseValue = useMemo(() => {
    return (value?: string): Date | null => {
      if (!value) return null;
      try {
        const date = new Date(value);
        return isNaN(date.getTime()) ? null : date;
      } catch (error) {
        console.warn('Date parsing failed:', error);
        return null;
      }
    };
  }, []);

  const formatValue = useMemo(() => {
    return (date: Date | null): string => {
      if (!date) return '';
      try {
        return format(date, 'yyyy-MM-dd');
      } catch (error) {
        console.warn('Date formatting to ISO failed:', error);
        return '';
      }
    };
  }, []);

  return {
    resolvedLocale,
    dateLocale,
    dateFormat,
    formatDisplayValue,
    parseValue,
    formatValue,
  };
};
